import { useState, useMemo, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Trash, Egg, ForkKnife, Hamburger, Cookie, Fire, CaretLeft, CaretRight, Camera, TextT, PencilSimple, Sparkle, X } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useCalorieTracking, CalorieEntry } from '../../hooks/useCalorieTracking';
import { GoalSetupModal } from './GoalSetupModal';
import { AddFoodModal } from './AddFoodModal';
import { FoodScanningModal } from './FoodScanningModal';
import { FoodFactsModal, FoodAnalysis } from './FoodFactsModal';
import { useTranslation } from '../../i18n';

interface CalorieTrackingViewProps {
  userId?: string;
  token?: string;
  refreshKey?: number;
  onSendMessage?: (message: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${theme.spacing.lg};
  padding-bottom: 100px;
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  margin-top: 16px;
`;

const SkeletonCircle = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    var(--color-bg-elevated) 0%,
    var(--color-border-subtle) 50%,
    var(--color-bg-elevated) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

// Day Navigation
const DayNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 20px;
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-bg-elevated);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: var(--color-border-subtle);
    color: var(--color-text);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const DaySelector = styled.div`
  display: flex;
  gap: 6px;
  padding: 0 8px;
`;

const DayButton = styled.button<{ $active: boolean; $hasData: boolean }>`
  width: 36px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? 'rgba(131, 76, 201, 0.2)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--color-primary)' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  position: relative;

  &:hover {
    background: var(--color-bg-elevated);
  }
`;

const DayLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 10px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
`;

const DayNumber = styled.div<{ $active: boolean }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-text)')};
`;

const DayIndicator = styled.div<{ $percent: number }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ $percent }) =>
    $percent >= 100 ? theme.colors.calorie.progress :
    $percent > 0 ? 'var(--color-primary)' :
    'transparent'};
  position: absolute;
  bottom: 4px;
`;

// Circular Progress
const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
`;

const ProgressSvg = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const ProgressCircle = styled.circle<{ $color: string }>`
  fill: none;
  stroke: ${({ $color }) => $color};
  stroke-width: 10;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease-out;
`;

const ProgressCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const RemainingCalories = styled.div<{ $isOver: boolean }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 36px;
  font-weight: 700;
  color: ${({ $isOver }) => ($isOver ? theme.colors.calorie.over : 'var(--color-text)')};
  line-height: 1;
`;

const RemainingLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  margin-top: 4px;
`;

const EditGoalButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-border-subtle);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 6px auto 0;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: var(--color-border);
  }
`;

const CalorieSummary = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 12px;
`;

const SummaryItem = styled.div`
  text-align: center;
`;

const SummaryValue = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const SummaryLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 11px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
`;

// Macro Bars
const MacroSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const MacroRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MacroLabel = styled.div`
  min-width: 80px;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: var(--color-text);
  white-space: nowrap;
`;

const MacroBarContainer = styled.div`
  flex: 1;
  height: 8px;
  background: var(--color-border-subtle);
  border-radius: 4px;
  overflow: hidden;
`;

const MacroBarFill = styled.div<{ $percent: number; $color: string }>`
  height: 100%;
  width: ${({ $percent }) => Math.min($percent, 100)}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  transition: width 0.3s ease-out;
`;

const MacroValue = styled.div`
  width: 70px;
  text-align: right;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-secondary);
`;

// Meal Sections
const MealSection = styled.div`
  background: var(--color-bg-elevated);
  border-radius: 16px;
  margin-bottom: 12px;
`;

const MealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
`;

const MealInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const MealIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--color-border-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary-light);
  flex-shrink: 0;
`;

const MealDetails = styled.div`
  min-width: 0;
  flex: 1;
`;

const MealName = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
`;

const MealCalories = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const MealItems = styled.div`
  border-top: 1px solid var(--color-bg-elevated);
`;

const FoodItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-bg-elevated);

  &:last-child {
    border-bottom: none;
  }
`;

const FoodInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 12px;
`;

const FoodName = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.3;
`;

const FoodServing = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 11px;
  color: var(--color-text-muted);
  margin-top: 4px;
  line-height: 1.3;
`;

const InsightButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 3px 8px 3px 6px;
  border-radius: 20px;
  background: rgba(131, 76, 201, 0.15);
  border: 1px solid rgba(131, 76, 201, 0.3);
  color: var(--color-primary-light);
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 11px;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(131, 76, 201, 0.25);
  }
`;

const InsightPopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
`;

const InsightPopupCard = styled.div`
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--color-bg);
  border-radius: 20px 20px 0 0;
  padding: 20px 20px max(20px, env(safe-area-inset-bottom));
`;

const InsightPopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const InsightPopupTitle = styled.div`
  flex: 1;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary-light);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const InsightPopupClose = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-bg-elevated);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  cursor: pointer;
`;

const InsightPopupText = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.6;
`;

const FoodCalories = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary-light);
  margin-right: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(244, 67, 54, 0.1);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.calorie.over};
  cursor: pointer;
  opacity: 0.6;
  transition: all ${theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    background: rgba(244, 67, 54, 0.2);
  }
`;

const RecommendedText = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AddButtonsContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const SmallButton = styled.button<{ $variant?: 'camera' | 'text' }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $variant }) =>
    $variant === 'camera' ? 'var(--color-primary)' : 'var(--color-border-subtle)'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $variant }) => $variant === 'camera' ? '#FFFFFF' : 'var(--color-text)'};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    opacity: 0.8;
  }
`;

const MEAL_ICONS = {
  breakfast: Egg,
  lunch: ForkKnife,
  dinner: Hamburger,
  snacks: Cookie,
};

const MEAL_RECOMMENDED = {
  breakfast: '400-600',
  lunch: '600-800',
  dinner: '600-800',
  snacks: '200-400',
};

export const CalorieTrackingView: React.FC<CalorieTrackingViewProps> = ({ userId, token, refreshKey, onSendMessage }) => {
  const { t, tArray, lang } = useTranslation();
  const {
    goal,
    weekData,
    selectedDate,
    currentDaySummary,
    isLoading,
    needsSetup,
    weekOffset,
    setSelectedDate,
    saveGoal,
    addEntry,
    deleteEntry,
    refetch,
    goToPreviousWeek,
    goToNextWeek,
  } = useCalorieTracking(userId, token);

  // Re-fetch calorie data when refreshKey changes (e.g., after nutrition survey completion)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  const [showGoalModal, setShowGoalModal] = useState(false);
  const [addFoodModal, setAddFoodModal] = useState<{
    isOpen: boolean;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks';
  }>({ isOpen: false, mealType: 'breakfast' });

  // Meal insights: entryId -> insight text (session only)
  const [mealInsights, setMealInsights] = useState<Map<number, string>>(new Map());
  const [insightPopup, setInsightPopup] = useState<string | null>(null);
  // Pending insight for the currently open FoodFactsModal
  const [pendingInsight, setPendingInsight] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(false);

  // Food capture states
  const [isScanningFood, setIsScanningFood] = useState(false);
  const [foodAnalysis, setFoodAnalysis] = useState<FoodAnalysis | null>(null);
  const [showFoodFacts, setShowFoodFacts] = useState(false);
  const [pendingFoodImage, setPendingFoodImage] = useState<string | null>(null);
  const [analysisSource, setAnalysisSource] = useState<'camera' | 'text'>('camera');
  const [scanError, setScanError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dayNamesArr = tArray('calorieTracking.dayNames');
  const monthNamesArr = tArray('calorieTracking.monthNames');

  // Generate 7-day date array based on weekOffset
  const dates = useMemo(() => {
    const result: { date: string; dayName: string; dayNum: number; monthShort: string }[] = [];
    const today = new Date();
    const endDay = new Date(today);
    endDay.setDate(endDay.getDate() + weekOffset * 7);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(endDay);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      result.push({
        date: dateStr,
        dayName: dayNamesArr[d.getDay()] || '',
        dayNum: d.getDate(),
        monthShort: monthNamesArr[d.getMonth()] || '',
      });
    }
    return result;
  }, [weekOffset, dayNamesArr, monthNamesArr]);

  // Calculate progress
  const dailyGoal = goal?.dailyCalorieGoal || 2000;
  const eaten = currentDaySummary?.totalCalories || 0;
  const remaining = dailyGoal - eaten;
  const isOver = remaining < 0;
  const progressPercent = Math.min((eaten / dailyGoal) * 100, 100);

  // Calculate macro goals (in grams)
  const carbsGoalGrams = Math.round((dailyGoal * (goal?.carbsGoalPercent || 50) / 100) / 4);
  const proteinGoalGrams = Math.round((dailyGoal * (goal?.proteinGoalPercent || 25) / 100) / 4);
  const fatGoalGrams = Math.round((dailyGoal * (goal?.fatGoalPercent || 25) / 100) / 9);

  // Safe macro values (handle NaN/undefined)
  const currentCarbs = Math.round(Number(currentDaySummary?.totalCarbs) || 0);
  const currentProtein = Math.round(Number(currentDaySummary?.totalProtein) || 0);
  const currentFat = Math.round(Number(currentDaySummary?.totalFat) || 0);

  // Circle dimensions
  const radius = 75;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progressPercent / 100) * circumference;

  // Resize image for API
  const resizeImage = (file: File, maxWidth = 1024, maxHeight = 1024, quality = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const getAutoMealType = (): 'breakfast' | 'lunch' | 'dinner' | 'snacks' => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'breakfast';
    if (hour >= 11 && hour < 15) return 'lunch';
    if (hour >= 15 && hour < 21) return 'dinner';
    return 'snacks';
  };

  const startInsightFetch = (analysis: typeof foodAnalysis) => {
    if (!analysis) return;
    setPendingInsight(null);
    setInsightError(false);
    setInsightLoading(true);
    fetch('/api/chat/calories/insight', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        foodName: analysis.name,
        mealType: getAutoMealType(),
        calories: analysis.calories,
        carbsGrams: analysis.macros.carbs.grams,
        proteinGrams: analysis.macros.protein.grams,
        fatGrams: analysis.macros.fat.grams,
        fiberGrams: analysis.nutrients.fiber,
        servingSize: analysis.servingSize,
        lang,
      }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text();
          console.error('[MealInsight] HTTP error', r.status, text);
          setInsightError(true);
          return;
        }
        const data = await r.json();
        if (data.success && data.data?.insight) {
          setPendingInsight(data.data.insight);
        } else {
          setInsightError(true);
        }
      })
      .catch((err) => {
        console.error('[MealInsight] fetch error:', err);
        setInsightError(true);
      })
      .finally(() => setInsightLoading(false));
  };

  const handleCameraCapture = async (file: File) => {
    try {
      const previewUrl = URL.createObjectURL(file);
      const base64Image = await resizeImage(file);

      setIsScanningFood(true);
      setPendingFoodImage(previewUrl);
      setScanError(null);

      const response = await fetch('/api/chat/analyze-food', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ imageUrl: base64Image, lang }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze food');
      }

      setIsScanningFood(false);
      setFoodAnalysis(data.data);
      setAnalysisSource('camera');
      setShowFoodFacts(true);
      startInsightFetch(data.data);
    } catch (error: any) {
      console.error('Error analyzing food:', error);
      setIsScanningFood(false);
      setScanError(error.message || t('food.scanError'));
      setPendingFoodImage(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleCameraCapture(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLogToCalories = async (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks') => {
    if (!foodAnalysis) return;

    try {
      const entryId = await addEntry({
        mealType,
        foodName: foodAnalysis.name,
        servingSize: foodAnalysis.servingSize,
        calories: foodAnalysis.calories,
        carbsGrams: foodAnalysis.macros.carbs.grams,
        proteinGrams: foodAnalysis.macros.protein.grams,
        fatGrams: foodAnalysis.macros.fat.grams,
        fiberGrams: foodAnalysis.nutrients.fiber,
        source: analysisSource,
      });

      // Store the already-fetched insight for the meal list button
      if (entryId && pendingInsight) {
        setMealInsights((prev) => new Map(prev).set(entryId, pendingInsight));
      }

      // Clean up and close
      setShowFoodFacts(false);
      setFoodAnalysis(null);
      setPendingInsight(null);
      setInsightLoading(false);
      if (pendingFoodImage) URL.revokeObjectURL(pendingFoodImage);
      setPendingFoodImage(null);
    } catch (error) {
      console.error('Error logging to calories:', error);
      alert('Failed to log food. Please try again.');
    }
  };

  const handleTextAnalysisComplete = (analysis: FoodAnalysis) => {
    setFoodAnalysis(analysis);
    setAnalysisSource('text');
    setShowFoodFacts(true);
    startInsightFetch(analysis);
  };

  const handleOpenAddFood = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks') => {
    setAddFoodModal({ isOpen: true, mealType });
  };

  const handleOpenCamera = (_mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks') => {
    // Meal type will be selected in FoodFactsModal
    fileInputRef.current?.click();
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (window.confirm(t('calorieTracking.deleteEntry'))) {
      await deleteEntry(entryId);
    }
  };

  const getDateProgress = (date: string) => {
    const summary = weekData.get(date);
    if (!summary || !goal) return 0;
    return (summary.totalCalories / goal.dailyCalorieGoal) * 100;
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <SkeletonCircle />
          <LoadingText>{t('calorieTracking.loading')}</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  // Show setup modal on first visit
  if (needsSetup && !showGoalModal) {
    setShowGoalModal(true);
  }

  return (
    <Container>
      {/* Hidden file input for camera */}
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
      />

      <GoalSetupModal
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        onSave={saveGoal}
        initialGoal={goal?.dailyCalorieGoal}
      />

      <AddFoodModal
        isOpen={addFoodModal.isOpen}
        onClose={() => setAddFoodModal({ ...addFoodModal, isOpen: false })}
        mealType={addFoodModal.mealType}
        onCameraCapture={(file) => {
          setAddFoodModal({ ...addFoodModal, isOpen: false });
          handleCameraCapture(file);
        }}
        onTextAnalysisComplete={handleTextAnalysisComplete}
        lang={lang}
      />

      <FoodScanningModal isOpen={isScanningFood} imageUrl={pendingFoodImage || ''} />

      {scanError && (
        <div style={{
          position: 'fixed', bottom: 80, left: 16, right: 16, zIndex: 9999,
          background: 'rgba(220, 50, 50, 0.95)', color: '#fff', padding: '12px 16px',
          borderRadius: 12, fontSize: 14, fontFamily: theme.typography.fontFamily.primary,
          textAlign: 'center', animation: 'fadeIn 0.2s ease',
        }}
          onClick={() => setScanError(null)}
        >
          {scanError}
        </div>
      )}

      <FoodFactsModal
        isOpen={showFoodFacts}
        onClose={() => {
          setShowFoodFacts(false);
          setFoodAnalysis(null);
          if (pendingFoodImage) URL.revokeObjectURL(pendingFoodImage);
          setPendingFoodImage(null);
        }}
        foodData={foodAnalysis}
        onTalkWithSoul={() => {
          if (foodAnalysis && onSendMessage) {
            const msg = lang === 'tr'
              ? `${foodAnalysis.name} (${foodAnalysis.servingSize}) yedim. ${foodAnalysis.calories} kcal, ${foodAnalysis.macros.carbs.grams}g karbonhidrat, ${foodAnalysis.macros.protein.grams}g protein, ${foodAnalysis.macros.fat.grams}g yağ. Bu öğün hakkında ne düşünüyorsun?`
              : `I ate ${foodAnalysis.name} (${foodAnalysis.servingSize}). ${foodAnalysis.calories} kcal, ${foodAnalysis.macros.carbs.grams}g carbs, ${foodAnalysis.macros.protein.grams}g protein, ${foodAnalysis.macros.fat.grams}g fat. What do you think about this meal?`;
            onSendMessage(msg);
          }
          setShowFoodFacts(false);
        }}
        showLogToCalories={true}
        onLogToCalories={handleLogToCalories}
        insightLoading={insightLoading}
        insightText={pendingInsight}
        insightError={insightError}
        onRetryInsight={() => startInsightFetch(foodAnalysis)}
      />

      {/* Day Navigation */}
      <DayNavigation>
        <NavButton type="button" onClick={goToPreviousWeek} aria-label="Previous week">
          <CaretLeft size={18} />
        </NavButton>
        <DaySelector>
          {dates.map((d) => (
            <DayButton
              key={d.date}
              $active={selectedDate === d.date}
              $hasData={(weekData.get(d.date)?.totalCalories || 0) > 0}
              onClick={() => setSelectedDate(d.date)}
            >
              <DayLabel>{d.dayName}</DayLabel>
              <DayNumber $active={selectedDate === d.date}>{d.dayNum}</DayNumber>
              <DayIndicator $percent={getDateProgress(d.date)} />
            </DayButton>
          ))}
        </DaySelector>
        <NavButton type="button" onClick={goToNextWeek} disabled={weekOffset >= 0} aria-label="Next week">
          <CaretRight size={18} />
        </NavButton>
      </DayNavigation>

      {/* Circular Progress */}
      <ProgressSection>
        <CircularProgress>
          <ProgressSvg viewBox="0 0 180 180">
            {/* Background circle */}
            <ProgressCircle
              cx="90"
              cy="90"
              r={radius}
              $color="var(--color-border-subtle)"
            />
            {/* Progress circle */}
            <ProgressCircle
              cx="90"
              cy="90"
              r={radius}
              $color={isOver ? theme.colors.calorie.over : theme.colors.calorie.progress}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </ProgressSvg>
          <ProgressCenter>
            <RemainingCalories $isOver={isOver}>
              {Math.abs(remaining)}
            </RemainingCalories>
            <RemainingLabel>{isOver ? t('calorieTracking.over') : t('calorieTracking.remaining')}</RemainingLabel>
            <EditGoalButton type="button" onClick={() => setShowGoalModal(true)} aria-label="Edit calorie goal">
              <PencilSimple size={12} color="var(--color-text-secondary)" />
            </EditGoalButton>
          </ProgressCenter>
        </CircularProgress>

        <CalorieSummary>
          <SummaryItem>
            <SummaryValue>{eaten}</SummaryValue>
            <SummaryLabel>{t('calorieTracking.eaten')}</SummaryLabel>
          </SummaryItem>
          <SummaryItem>
            <SummaryValue>
              <Fire size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              -
            </SummaryValue>
            <SummaryLabel>{t('calorieTracking.burned')}</SummaryLabel>
          </SummaryItem>
        </CalorieSummary>

      </ProgressSection>

      {/* Macro Bars */}
      <MacroSection>
        <MacroRow>
          <MacroLabel>{t('calorieTracking.carbs')}</MacroLabel>
          <MacroBarContainer>
            <MacroBarFill
              $percent={carbsGoalGrams > 0 ? (currentCarbs / carbsGoalGrams) * 100 : 0}
              $color={theme.colors.calorie.macros.carbs}
            />
          </MacroBarContainer>
          <MacroValue>
            {currentCarbs}/{carbsGoalGrams}g
          </MacroValue>
        </MacroRow>
        <MacroRow>
          <MacroLabel>{t('calorieTracking.protein')}</MacroLabel>
          <MacroBarContainer>
            <MacroBarFill
              $percent={proteinGoalGrams > 0 ? (currentProtein / proteinGoalGrams) * 100 : 0}
              $color={theme.colors.calorie.macros.protein}
            />
          </MacroBarContainer>
          <MacroValue>
            {currentProtein}/{proteinGoalGrams}g
          </MacroValue>
        </MacroRow>
        <MacroRow>
          <MacroLabel>{t('calorieTracking.fat')}</MacroLabel>
          <MacroBarContainer>
            <MacroBarFill
              $percent={fatGoalGrams > 0 ? (currentFat / fatGoalGrams) * 100 : 0}
              $color={theme.colors.calorie.macros.fat}
            />
          </MacroBarContainer>
          <MacroValue>
            {currentFat}/{fatGoalGrams}g
          </MacroValue>
        </MacroRow>
      </MacroSection>

      {/* Insight popup for meal list button */}
      {insightPopup && (
        <InsightPopupOverlay onClick={() => setInsightPopup(null)}>
          <InsightPopupCard onClick={(e) => e.stopPropagation()}>
            <InsightPopupHeader>
              <Sparkle size={16} weight="fill" color={'var(--color-primary-light)'} />
              <InsightPopupTitle>{t('food.soulInsight')}</InsightPopupTitle>
              <InsightPopupClose type="button" onClick={() => setInsightPopup(null)} aria-label="Close insight">
                <X size={14} />
              </InsightPopupClose>
            </InsightPopupHeader>
            <InsightPopupText>{insightPopup}</InsightPopupText>
          </InsightPopupCard>
        </InsightPopupOverlay>
      )}

      {/* Meal Sections */}
      {(['breakfast', 'lunch', 'dinner', 'snacks'] as const).map((mealType) => {
        const Icon = MEAL_ICONS[mealType];
        const mealName = t(`calorieTracking.${mealType}`);
        const recommended = MEAL_RECOMMENDED[mealType];
        const entries = currentDaySummary?.meals[mealType] || [];
        const mealCalories = entries.reduce((sum, e) => sum + e.calories, 0);

        return (
          <MealSection key={mealType}>
            <MealHeader>
              <MealInfo>
                <MealIcon>
                  <Icon size={20} />
                </MealIcon>
                <MealDetails>
                  <MealName>{mealName}</MealName>
                  {entries.length > 0 ? (
                    <MealCalories>{mealCalories} kcal</MealCalories>
                  ) : (
                    <RecommendedText>{t('calorieTracking.recommended')}: {recommended} kcal</RecommendedText>
                  )}
                </MealDetails>
              </MealInfo>
              <AddButtonsContainer>
                <SmallButton $variant="camera" type="button" onClick={() => handleOpenCamera(mealType)} title="Take photo" aria-label="Take photo">
                  <Camera size={16} weight="bold" />
                </SmallButton>
                <SmallButton $variant="text" type="button" onClick={() => handleOpenAddFood(mealType)} title="Type food" aria-label="Type food">
                  <TextT size={16} weight="bold" />
                </SmallButton>
              </AddButtonsContainer>
            </MealHeader>

            {entries.length > 0 && (
              <MealItems>
                {entries.map((entry: CalorieEntry) => {
                  const insight = entry.id ? mealInsights.get(entry.id) : undefined;
                  return (
                    <FoodItem key={entry.id}>
                      <FoodInfo>
                        <FoodName>{entry.foodName}</FoodName>
                        {entry.servingSize && <FoodServing>{entry.servingSize}</FoodServing>}
                        {insight && (
                          <InsightButton onClick={() => setInsightPopup(insight)}>
                            <Sparkle size={11} weight="fill" />
                            {t('food.insightButton')}
                          </InsightButton>
                        )}
                      </FoodInfo>
                      <FoodCalories>{entry.calories} kcal</FoodCalories>
                      <DeleteButton type="button" onClick={() => entry.id && handleDeleteEntry(entry.id)} aria-label="Delete entry">
                        <Trash size={14} />
                      </DeleteButton>
                    </FoodItem>
                  );
                })}
              </MealItems>
            )}
          </MealSection>
        );
      })}
    </Container>
  );
};
